import { z } from 'zod';
import { DiscountPctSchema, HistoryEntrySchema, IDSchema, IsoTimestampSchema, MoneyCentsSchema, QuoteIdSchema, SchemaVersionSchema, } from './shared.js';
import { ProductKeySchema } from './quote.js';
import { DAYS_MAX, PCT_MAX, QUANTITY_MAX, TEXT_LIMITS } from '../limits.js';
export const ApprovalRequestTypeSchema = z.enum([
    'pilot',
    'trial',
    'additional_discount',
    'non_standard_terms',
    'scope_expansion',
    // RIG-604 — a rep asks for the platform setup fee to be reduced or waived.
    'setup_fee_waiver',
]);
export const ApprovalStateSchema = z.enum(['pending', 'approved', 'denied', 'expired']);
// QA 2026-10-01 F-08 / F-22 — approval payloads are request input (the
// approvals route safeParses them), so every number and string is bounded.
const ProductScopeSchema = z.array(ProductKeySchema).max(ProductKeySchema.options.length);
const PayloadTextSchema = z.string().max(TEXT_LIMITS.justification);
export const PilotPayloadSchema = z.object({
    pilotScope: ProductScopeSchema,
    estimatedACV: MoneyCentsSchema,
    duration: z.literal(90),
});
export const TrialPayloadSchema = z.object({
    trialScope: ProductScopeSchema,
    durationDays: z.number().int().positive().max(DAYS_MAX),
    productCaps: z.record(z.number().int().positive().max(QUANTITY_MAX)),
    estimatedConversionACV: MoneyCentsSchema,
    conversionRationale: PayloadTextSchema,
    accountJustification: PayloadTextSchema,
});
export const AdditionalDiscountPayloadSchema = z.object({
    baseACV: MoneyCentsSchema,
    requestedDiscountPct: DiscountPctSchema,
    requestedDiscountAmount: MoneyCentsSchema,
    resultingACV: MoneyCentsSchema,
    beyondStandardLadder: z.object({
        standardVolumeTier: z.number().int().nonnegative().max(PCT_MAX),
        standardBundleTier: z.number().int().nonnegative().max(PCT_MAX),
        standardTotalDiscountPct: z.number().int().nonnegative().max(PCT_MAX),
    }),
});
export const NonStandardTermsPayloadSchema = z.object({
    proposedTerm: PayloadTextSchema,
    customerJustification: PayloadTextSchema,
});
export const ScopeExpansionPayloadSchema = z.object({
    description: PayloadTextSchema,
    estimatedAdditionalACV: MoneyCentsSchema,
});
/**
 * RIG-604 — setup fee waiver. All three amounts are integer cents.
 * `requestedSetupFee` is the fee the rep wants to charge (0 is a full waiver)
 * and `reductionAmount` is `standardSetupFee - requestedSetupFee`.
 */
export const SetupFeeWaiverPayloadSchema = z
    .object({
    standardSetupFee: MoneyCentsSchema,
    requestedSetupFee: MoneyCentsSchema,
    reductionAmount: MoneyCentsSchema,
})
    .refine((p) => p.requestedSetupFee < p.standardSetupFee, {
    message: 'The requested setup fee needs to be lower than the standard setup fee.',
    path: ['requestedSetupFee'],
})
    .refine((p) => p.reductionAmount === p.standardSetupFee - p.requestedSetupFee, {
    message: 'The reduction needs to equal the standard setup fee minus the requested setup fee.',
    path: ['reductionAmount'],
});
export const ApprovalPayloadSchema = z.union([
    PilotPayloadSchema,
    TrialPayloadSchema,
    AdditionalDiscountPayloadSchema,
    NonStandardTermsPayloadSchema,
    ScopeExpansionPayloadSchema,
    SetupFeeWaiverPayloadSchema,
]);
export const ApprovalSchema = z.object({
    id: IDSchema,
    type: z.literal('approval'),
    schemaVersion: SchemaVersionSchema,
    accountId: IDSchema,
    requestType: ApprovalRequestTypeSchema,
    state: ApprovalStateSchema,
    stateChangedAt: IsoTimestampSchema,
    stateChangedBy: z.string(),
    requestedBy: z.string(),
    requestedAt: IsoTimestampSchema,
    justification: z.string(),
    quoteId: QuoteIdSchema.optional(),
    dealId: IDSchema.optional(),
    submissionId: IDSchema.optional(),
    reviewedBy: z.string().optional(),
    reviewedAt: IsoTimestampSchema.optional(),
    decisionNote: z.string().optional(),
    payload: ApprovalPayloadSchema,
    expiresAt: IsoTimestampSchema,
    reminderFiredAt7d: IsoTimestampSchema.optional(),
    reminderFiredAt12d: IsoTimestampSchema.optional(),
    // RIG-336 — uses the shared HistoryEntrySchema (payload omitted: this entity
    // intentionally does not record structured history payloads).
    history: z.array(HistoryEntrySchema.omit({ payload: true })).default([]),
    // RIG-380 — human-readable reference (e.g. `APPR-00231`). Additive layer over
    // the opaque PK; NOT NULL + unique. Render via the shared display helpers.
    reference: z.string(),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    version: z.number().int().positive(),
});
//# sourceMappingURL=approval.js.map